import axios from "axios";
import { UST_DATABASE, CAPACITIES, rankTanks, solveDiameter, calculateVolume, solveInchesFromVolume, tankKey, productName, tankLabel } from "@/lib/tank";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ERRORS = {
  400: "That wasn't a readable JPEG or PNG. Try again.",
  413: "That photo is too large. Take a smaller one.",
  422: "Couldn't read that screen. Shoot the inventory numbers straight on and try again.",
  429: "Too many scans. Wait a moment and try again.",
};

export async function scanVeeder(image) {
  try {
    const { data } = await axios.post(`${API}/veeder/scan`, { image }, { timeout: 90000 });
    return { ok: true, readings: data.readings || [] };
  } catch (e) {
    const status = e.response?.status;
    if (!e.response) return { ok: false, error: "The reader didn't answer. Check your signal and try again." };
    return { ok: false, error: ERRORS[status] || "The reader didn't answer. Try the photo again." };
  }
}

export function mapProduct(p) {
  if (!p) return null;
  const s = p.toUpperCase();
  if (s.includes("DEF")) return "DEF";
  if (["KERO", "K-1"].some((k) => s.includes(k))) return "Kerosene";
  if (["DSL", "DIESEL", "ULSD"].some((k) => s.includes(k)) || /\b#2\b/.test(s)) return "Diesel #2 (ULSD)";
  if (["PREM", "SUPER", "93", "91"].some((k) => s.includes(k))) return "Premium 93";
  if (["MID", "PLUS", "89"].some((k) => s.includes(k))) return "Midgrade 89";
  if (["REG", "UNL", "87"].some((k) => s.includes(k))) return "Regular 87";
  return null;
}

function exactTank(h, gal, full) {
  let best = null;
  for (const t of UST_DATABASE.filter((x) => Math.abs(x.capacity - full) / full <= 0.03)) {
    const d = solveDiameter(h, gal, t.capacity, t.ends);
    if (d == null) continue;
    const off = Math.abs(d - t.diameter);
    if (off <= 2 && (!best || off < best.off)) best = { spec: t, off };
  }
  if (best) return { ...best.spec, catalog: true };
  const d = solveDiameter(h, gal, full, "flat");
  return d != null && d >= 24 && d <= 240 ? { capacity: full, diameter: Math.round(d * 10) / 10, ends: "flat", catalog: false } : null;
}

function nearestCapacity(target) {
  let best = null, bestOff = Infinity;
  for (const c of CAPACITIES) {
    const off = Math.abs(c - target) / c;
    if (off < bestOff) { bestOff = off; best = c; }
  }
  return best != null && bestOff <= 0.08 ? best : null;
}

const tankWord = (l, t) => { const x = tankLabel(l); const m = x.match(/^Tank (\d+)(.*)$/); return m ? t("Tank {n}", { n: m[1] }) + m[2] : x; };
// One name per product everywhere: the app's full name when the slip text maps to a grade.
export const productLabel = (raw, t) => { const id = mapProduct(raw); return id ? t(productName(id)) : raw; };
export const readingTitle = (r, t) => [tankWord(r.tankLabel, t) || t("Tank"), productLabel(r.product, t)].filter(Boolean).join(" · ");

const pos = (v) => v != null && v > 0;
const n0 = (v) => Math.round(v).toLocaleString("en-US");
const wide = (h, g, cap, ends) => { const d = solveDiameter(h, g, cap, ends); return d == null ? "?" : d.toFixed(0); };

function capWhy(r, g, full, t) {
  if (r.capacityGallons != null && r.capacityGallons > g) return t("Size {c} gal is printed on the slip.", { c: n0(full) });
  if (pos(r.ullageGallons)) return t("{g} gal in + {u} gal room = {c} gal tank.", { g: n0(g), u: n0(r.ullageGallons), c: n0(full) });
  return t("{g} gal in + {u} gal room to 90% = {s} gal, which is 90% of {c} gal.", { g: n0(g), u: n0(r.ullage90Gallons), s: n0(g + r.ullage90Gallons), c: n0(full) });
}

export function afterCheck(r, tank, t) {
  const aH = r.afterInches, aG = r.afterGallons;
  if (!pos(aH) || !pos(aG) || !tank) return null;
  const { capacity: cap, diameter: d, ends } = tank;
  const off = Math.abs(calculateVolume(aH, d, cap, ends) - aG);
  if (off <= Math.max(60, cap * 0.02)) {
    return { ok: true, text: t("After reading checks out too ({h}\" = {g} gal, {o} gal off).", { h: aH.toFixed(2), g: n0(aG), o: n0(off) }) };
  }
  return {
    ok: false,
    text: t("Before and after don't fit one tank size. Before points to about {b}\" wide, after to about {a}\". Don't trust this size — load the store's tank chart or the real diameter.",
      { b: wide(r.inches, r.gallons, cap, ends), a: wide(aH, aG, cap, ends) }),
  };
}

function finish(r, plan, tank, t) {
  const chk = afterCheck(r, tank, t);
  if (!chk) return plan;
  return { ...plan, tone: chk.ok ? plan.tone : "danger", check: chk };
}

// Keep the printout's own numbers so Plan a drop starts from the console's gallons, and clear any old drop.
export function planFromReading(r, t) {
  const plan = { ...basePlan(r, t), water: r.waterInches > 0 ? r.waterInches : null };
  const p = plan.patch;
  if (p.startInches == null) return plan;
  const tank = { capacity: p.capacity, diameter: p.diameter, ends: p.ends };
  const num = (v) => (v != null && v >= 0 ? Math.round(v * 100) / 100 : null);
  const scan = { inches: p.startInches, gallons: num(r.gallons), ullage: num(r.ullageGallons), ullage90: num(r.ullage90Gallons),
    water: num(r.waterInches), temp: num(r.tempF), label: (r.tankLabel || "").slice(0, 30),
    product: mapProduct(r.product) || (r.product || "").slice(0, 30), key: tank.capacity ? tankKey(tank) : null, at: new Date().toISOString() };
  return { ...plan, patch: { ...p, dropGallons: 0, scan } };
}

function fromGallons(r, g, t, patch, title, water) {
  const full = pos(r.ullageGallons) ? g + r.ullageGallons : pos(r.ullage90Gallons) ? (g + r.ullage90Gallons) / 0.9 : null;
  if (!full) return null;
  const near = nearestCapacity(full);
  if (Math.abs(near - full) / full > 0.04) return null;
  const tank = UST_DATABASE.find((x) => x.capacity === near && x.diameter === 96) || UST_DATABASE.find((x) => x.capacity === near);
  if (!tank) return null;
  const h = Math.round(solveInchesFromVolume(g, tank.diameter, tank.capacity, tank.ends) * 8) / 8;
  return {
    patch: { ...patch, capacity: tank.capacity, diameter: tank.diameter, ends: tank.ends, startInches: h }, tab: "calc", tone: "caution",
    why: capWhy(r, g, Math.round(full), t),
    note: `${title}. ${t("No height on the slip. Picked a {c} gal · {d}\" tank from gallons + ullage and worked the stick out to {h}\". Stick the tank to confirm.", { c: n0(tank.capacity), d: tank.diameter, h: h.toFixed(2) })}${water}`,
  };
}

function basePlan(r, t) {
  const product = mapProduct(r.product);
  const h = r.inches, g = r.gallons;
  const patch = {};
  if (product) patch.product = product;
  if (pos(g)) patch.solverGallons = Math.round(g);
  if (pos(h)) patch.solverInches = h;
  const bits = [tankWord(r.tankLabel, t), productLabel(r.product, t), pos(h) && `${h.toFixed(2)}"`, pos(g) && `${Math.round(g).toLocaleString("en-US")} gal`].filter(Boolean);
  const title = bits.length ? `Veeder-Root · ${bits.join(" · ")}` : t("Veeder-Root photo");
  const water = "";

  if (!pos(h) && !pos(g)) {
    return { patch, tab: "calc", tone: "caution", note: `${t("Couldn't find a fuel height or gallon volume on that photo.")} ${t("Fill the frame with the inventory line and shoot it straight on.")}${water}` };
  }
  if (!pos(h) && pos(g)) {
    const byG = fromGallons(r, g, t, patch, title, water);
    if (byG) return byG;
  }
  if (!(pos(h) && pos(g))) {
    const hasH = pos(h);
    return {
      patch: hasH ? { ...patch, startInches: h } : patch, tab: hasH ? "calc" : "solver",
      tone: r.confidence === "high" ? "safe" : "caution",
      note: `${title}. ${t(hasH ? "Stick is loaded on the predictor." : "Gallons are loaded on the solver.")}${water}`,
    };
  }
  let full = null;
  if (r.capacityGallons != null && r.capacityGallons > g) full = Math.round(r.capacityGallons);
  else if (pos(r.ullageGallons)) full = Math.round(g + r.ullageGallons);
  else if (pos(r.ullage90Gallons)) full = Math.round((g + r.ullage90Gallons) / 0.9);

  const exact = full != null ? exactTank(h, g, full) : null;
  if (exact) {
    const what = exact.catalog
      ? t("Matched {c} gal · {d}\" ({k})", { c: exact.capacity.toLocaleString("en-US"), d: exact.diameter, k: t(exact.ends === "dome" ? "fiberglass" : "steel") })
      : t("Worked out a {c} gal tank, {d}\" wide", { c: exact.capacity.toLocaleString("en-US"), d: exact.diameter.toFixed(1) });
    const why = `${capWhy(r, g, full, t)} ${t("At {h}\", {g} gal fits a tank about {d}\" wide.", { h: h.toFixed(2), g: n0(g), d: wide(h, g, exact.capacity, exact.ends) })}${exact.catalog ? ` ${t("Closest standard size: {d}\".", { d: exact.diameter })}` : ""}`;
    return finish(r, {
      patch: { ...patch, capacity: exact.capacity, diameter: exact.diameter, ends: exact.ends, startInches: h },
      tab: "calc", tone: "safe", why,
      note: `${title}. ${what} ${t("from the volume and ullage on the printout.")} ${t("Set the drop and read the predicted stick.")}${water}`,
    }, exact, t);
  }
  const capHint = r.capacityGallons != null ? nearestCapacity(r.capacityGallons) : pos(r.ullageGallons) ? nearestCapacity(g + r.ullageGallons) : null;
  let hits = rankTanks(h, g, capHint != null ? String(capHint) : "ANY");
  if (!hits.length) hits = rankTanks(h, g, "ANY");
  const top = hits[0];
  if (!top || top.deltaPct >= 12) {
    return { patch: { ...patch, solverMode: "level" }, tab: "solver", tone: "caution", note: `${title}. ${t("No standard tank was close enough to load on its own. Pick a profile below.")}${water}` };
  }
  const why = t("No tank size on the slip. Of the standard tanks, {c} gal · {d}\" comes closest to {g} gal at {h}\". This is a guess.", { c: n0(top.capacity), d: top.diameter, g: n0(g), h: h.toFixed(2) });
  return finish(r, {
    patch: { ...patch, capacity: top.capacity, diameter: top.diameter, ends: top.ends, startInches: h },
    tab: "calc", tone: top.deltaPct < 5 ? "safe" : "caution", why,
    note: `${title}. ${t("Matched {c} gal · {d}\" (model was {o} gal off).", { c: top.capacity.toLocaleString("en-US"), d: top.diameter, o: Math.round(top.delta).toLocaleString("en-US") })} ${t("Set the drop and read the predicted stick.")}${water}`,
  }, top, t);
}

export function fileToJpegBase64(file, max = 1600) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.85).split(",")[1]);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}
